import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { GridRowModes } from '@mui/x-data-grid';
import { handleValidateCodevalue } from '../../Utility/constant';
import { createRequestData, getRequestData, updateRequestData } from '../../Utility/apiHelper';
import { requestHideLoader, requestShowLoader } from '../../Store/Global/action';


export const useEmailConfig = ({ getUrl, saveUrl, fromType, replyToType,selectedUser}) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const internationalise = useSelector(state => state?.internationalise?.currentHashMapData);
    const isLoading = useSelector(state => state?.global?.isLoading);

    const [fromDataGridData, setFromDataGridData] = useState([]);
    const [replyToDataGridData, setReplyToDataGridData] = useState([]);
    const [isEditFromPage, setIsEditFromPage] = useState(false);
    const [isEditReplyToPage, setIsEditReplyToPage] = useState(false);

    const [fromRowModesModel, setFromRowModesModel] = useState({});
    const [replyToRowModesModel, setReplyToRowModesModel] = useState({});

    const [sameAsFrom, setSameAsFrom] = useState(false);

    const [selectedFolders, setSelectedFolders] = useState([{name : "INBOX"}]);
    const [availableFolders, setAvailableFolders] = useState([]);
    const [foldersLoading, setFoldersLoading] = useState(false);

    const [replyToSelectedFolders, setReplyToSelectedFolders] = useState([{name : "INBOX"}]);
    const [replyToAvailableFolders, setReplyToAvailableFolders] = useState([]);
    const [replyToFoldersLoading, setReplyToFoldersLoading] = useState(false);



    const getEmptyRow = useCallback((emailType) => ({
        id: `new-${emailType}`,
        emailType: emailType,
        smtpHost: "",
        smtpPort: "",
        smtpUsername: "",
        smtpPassword: "",
        imapHost : "",
        imapPort : ""
    }), []);

    const getEmailCredentials = useCallback(async () => {
        try {
            dispatch(requestShowLoader());
            const url = `${process.env.REACT_APP_BASE_URL}${getUrl}`;
            const response = await getRequestData(url);
            
            if (response?.status === 200 && response?.data?.success) {
                const configs = response?.data?.data || [];
                
                const fromConfig = configs.find(c => c.emailType === fromType);
                const replyToConfig = configs.find(c => c.emailType === replyToType);
                
                if (fromConfig) {
                    setFromDataGridData([{
                        ...fromConfig,
                        emailType: fromType,
                    }]);
                    setIsEditFromPage(true);
                    
                    if (fromConfig.emailMetadata) {
                        try {
                            const metadata = JSON.parse(fromConfig.emailMetadata);
                            if (metadata.folder_list && Array.isArray(metadata.folder_list)) {
                                setSelectedFolders(metadata.folder_list.map(f => ({ name: f })));
                            }
                        } catch (e) {
                            console.error('Failed to parse emailMetadata:', e);
                        }
                    }
                    
                    if (fromConfig.imapHost && fromConfig.imapPort && fromConfig.smtpUsername && fromConfig.smtpPassword) {
                        fetchImapFolders(fromConfig.imapHost, fromConfig.imapPort, fromConfig.smtpUsername, fromConfig.smtpPassword);
                    }
                } else {
                    setFromDataGridData([getEmptyRow(fromType)]);
                    setIsEditFromPage(false);
                }
                
                if (replyToConfig) {
                    setReplyToDataGridData([{
                        ...replyToConfig,
                        emailType: replyToType,
                    }]);
                    setIsEditReplyToPage(true);
                    
                    if (replyToConfig.emailMetadata) {
                        try {
                            const metadata = JSON.parse(replyToConfig.emailMetadata);
                            if (metadata.folder_list && Array.isArray(metadata.folder_list)) {
                                setReplyToSelectedFolders(metadata.folder_list.map(f => ({ name: f })));
                            }
                        } catch (e) {
                            console.error('Failed to parse Reply-To emailMetadata:', e);
                        }
                    }
                    
                    if (replyToConfig.imapHost && replyToConfig.imapPort && replyToConfig.smtpUsername && replyToConfig.smtpPassword) {
                        fetchReplyToImapFolders(replyToConfig.imapHost, replyToConfig.imapPort, replyToConfig.smtpUsername, replyToConfig.smtpPassword);
                    }
                } else {
                    setReplyToDataGridData([getEmptyRow(replyToType)]);
                    setIsEditReplyToPage(false);
                }
            } else {
                setFromDataGridData([getEmptyRow(fromType)]);
                setReplyToDataGridData([getEmptyRow(replyToType)]);
            }
        } catch {
        } finally {
            dispatch(requestHideLoader());
        }
    }, [selectedUser,getUrl, fromType, replyToType, dispatch, getEmptyRow]);

    useEffect(() => {
        if(selectedUser!=null || selectedUser != undefined){
        getEmailCredentials();
        }
        else{
        setFromDataGridData([getEmptyRow(fromType)]);
        setReplyToDataGridData([getEmptyRow(fromType)]);
        }
    }, [getEmailCredentials,selectedUser]);

    const saveEmailCredentials = useCallback(async (row) => {
        try {
            dispatch(requestShowLoader());
            const url = `${process.env.REACT_APP_BASE_URL}${saveUrl}`;
            const payload = { ...row };
            
            if (payload.id && typeof payload.id === 'string' && payload.id.startsWith('new-')) {
                delete payload.id;
            }
            
            const response = await createRequestData(payload, url);
            if (response?.status >= 200 || response?.status >= 300) {
                if (response?.data?.success) {
                    enqueueSnackbar(handleValidateCodevalue(internationalise, response?.data?.message), { variant: "success" });
                    getEmailCredentials();
                    setSameAsFrom(false);
                } else {
                    enqueueSnackbar(handleValidateCodevalue(internationalise, response?.data?.message), { variant: "warning" });
                }
            } else {
                enqueueSnackbar(handleValidateCodevalue(internationalise, response?.propererror?.data?.message || "COMMON_PLEASE_TRY_AGAIN"), { variant: 'error' });
            }
        } catch {
        } finally {
            dispatch(requestHideLoader());
        }
    }, [saveUrl, dispatch, enqueueSnackbar, getEmailCredentials, internationalise]);

    const updateEmailCredentials = useCallback(async (row) => {
        try {
            dispatch(requestShowLoader());
            const url = `${process.env.REACT_APP_BASE_URL}${saveUrl}/${row?.id}`;
            const payload = { ...row };
            
            const response = await updateRequestData(payload, url);
            if (response?.status >= 200 || response?.status >= 300) {
                if (response?.data?.success) {
                    enqueueSnackbar(handleValidateCodevalue(internationalise, response?.data?.message), { variant: "success" });
                    setSameAsFrom(false);
                    getEmailCredentials();
                } else {
                    enqueueSnackbar(handleValidateCodevalue(internationalise, response?.data?.message), { variant: "warning" });
                }
            } else {
                enqueueSnackbar(handleValidateCodevalue(internationalise, response?.propererror?.data?.message || "COMMON_PLEASE_TRY_AGAIN"), { variant: 'error' });
            }
        } catch {
        } finally {
            dispatch(requestHideLoader());
        }
    }, [saveUrl,selectedUser, dispatch, enqueueSnackbar, internationalise]);

    const handleFromEditClick = useCallback((id) => () => {
        setFromRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
    }, []);

    const handleFromSaveClick = useCallback((id) => () => {
        setFromRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.View } }));
    }, []);

    const handleFromCancelClick = useCallback((id) => () => {
        setFromRowModesModel(prev => ({
            ...prev,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        }));
    }, []);

    const handleReplyToEditClick = useCallback((id) => () => {
        setReplyToRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
    }, []);

    const handleReplyToSaveClick = useCallback((id) => () => {
        setReplyToRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.View } }));
        setSameAsFrom(false);
    }, []);

    const handleReplyToCancelClick = useCallback((id) => () => {
        setReplyToRowModesModel(prev => ({
            ...prev,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        }));
        setSameAsFrom(false);
    }, []);

    const handleSameAsFromChange = useCallback((event) => {
        const checked = event.target.checked;
        setSameAsFrom(checked);
        
        if (checked && fromDataGridData.length > 0) {
            const fromData = fromDataGridData[0];
            const newReplyToData = [{
                id: replyToDataGridData[0]?.id || `new-${replyToType}`,
                emailType: replyToType,
                smtpHost: fromData.smtpHost || "",
                smtpPort: fromData.smtpPort || "",
                smtpUsername: fromData.smtpUsername || "",
                smtpPassword: fromData.smtpPassword || "",
                imapHost: "",
                imapPort: "",
            }];
            setReplyToDataGridData(newReplyToData);
            
            const rowId = newReplyToData[0].id;
            setReplyToRowModesModel({ [rowId]: { mode: GridRowModes.Edit } });
        }
    }, [fromDataGridData, replyToDataGridData, replyToType]);

    const processFromRowUpdate = useCallback((newRow, oldRow) => {
        if (!newRow?.smtpHost || !newRow?.smtpPort || !newRow?.smtpUsername || !newRow?.smtpPassword) {
            enqueueSnackbar(handleValidateCodevalue(internationalise, "COMMON_MSG_MANDATORY_FIELDS"), { variant: "error" });
            handleFromEditClick(newRow?.id)();
            return newRow;
        } else {
            const rowWithType = { ...newRow, emailType: fromType };
            isEditFromPage ? updateEmailCredentials(rowWithType) : saveEmailCredentials(rowWithType); 
        }
        const updatedRow = { ...newRow, isNew: false };
        setFromDataGridData(prev => prev.map((row) => (row.id === newRow.id ? updatedRow : row)));
        return updatedRow;
    }, [selectedUser,enqueueSnackbar, fromType, handleFromEditClick, internationalise, isEditFromPage, saveEmailCredentials, updateEmailCredentials]);

    // Fetch available IMAP folders from server
    const fetchImapFolders = useCallback(async (imapHost, imapPort, smtpUsername, smtpPassword) => {
        try {
            setFoldersLoading(true);
            const url = `${process.env.REACT_APP_BASE_URL}api/client/email-config/folders`;
            const payload = { imapHost, imapPort, smtpUsername, smtpPassword };
            const response = await createRequestData(payload, url);
            
            if (response?.data?.success && Array.isArray(response?.data?.data)) {
                setAvailableFolders(response.data.data.map(f => ({ name: f })));
            }
        } catch (e) {
            console.error('Failed to fetch IMAP folders:', e);
        } finally {
            setFoldersLoading(false);
        }
    }, []);

    const handleFolderChange = useCallback((event, newValue) => {
        // Check if "Select All" was clicked
        const selectAllClicked = newValue.some(v => v.name === 'SELECT_ALL');
        if (selectAllClicked) {
            setSelectedFolders(availableFolders);
        } else {
            setSelectedFolders(newValue);
        }
    }, [availableFolders]);
    
    const processIMapRowUpdate = useCallback(async (newRow, oldRow) => {
            
        if (!newRow?.imapHost || !newRow?.imapPort || !newRow?.smtpUsername || !newRow?.smtpPassword) {
            enqueueSnackbar(handleValidateCodevalue(internationalise, "COMMON_MSG_MANDATORY_FIELDS"), { variant: "error" });
            handleFromEditClick(newRow?.id)();
            return newRow;
        }
        
        // Only test connection and fetch folders - do NOT save
        try {
            setFoldersLoading(true);
            const testUrl = `${process.env.REACT_APP_BASE_URL}api/client/email-config/test`;
            const { id, ...testPayload } = newRow; 
            const testResponse = await createRequestData(testPayload, testUrl);
            
            if (testResponse?.data?.success) {
                enqueueSnackbar(handleValidateCodevalue(internationalise, testResponse?.data?.message), { variant: "success" });
                await fetchImapFolders(newRow.imapHost, newRow.imapPort, newRow.smtpUsername, newRow.smtpPassword);
                
                const updatedRow = { ...oldRow, ...newRow };
                setFromDataGridData(prev => prev.map((row) => (row.id === newRow.id ? updatedRow : row)));
                return updatedRow;
            } else {
                enqueueSnackbar(handleValidateCodevalue(internationalise, testResponse?.data?.message), { variant: "error" });
                handleFromEditClick(newRow?.id)();
                return newRow;
            }
        } catch (e) {
            enqueueSnackbar(handleValidateCodevalue(internationalise, "EMAIL_IMAP_CONNECTION_FAILED"), { variant: "error" });
            handleFromEditClick(newRow?.id)();
            return newRow;
        } finally {
            setFoldersLoading(false);
        }
    }, [enqueueSnackbar, handleFromEditClick, internationalise, fetchImapFolders, createRequestData]);

    const saveImapConfig = useCallback(async (assigneeId) => {
        const currentData = fromDataGridData?.[0];
        if (!currentData?.imapHost || !currentData?.imapPort || !currentData?.smtpUsername || !currentData?.smtpPassword) {
            enqueueSnackbar(handleValidateCodevalue(internationalise, "COMMON_MSG_MANDATORY_FIELDS"), { variant: "error" });
            return false;
        }
        
        const emailMetadata = JSON.stringify({
            folder_list: selectedFolders.map(f => f.name)
        });
        
        const payload = {
            ...currentData,
            emailType: fromType,
            defaultAssignee: assigneeId,
            emailMetadata
        };
        
        if (payload.id && typeof payload.id === 'string' && payload.id.startsWith('new-')) {
            delete payload.id;
        }
        
        try {
            if (isEditFromPage) {
                await updateEmailCredentials(payload);
            } else {
                await saveEmailCredentials(payload);
            }
            return true;
        } catch (e) {
            return false;
        }
    }, [fromDataGridData, selectedFolders, fromType, isEditFromPage, updateEmailCredentials, saveEmailCredentials, enqueueSnackbar, internationalise]);

    const processReplyToRowUpdate = useCallback((newRow, oldRow) => {
        if (!newRow?.smtpHost || !newRow?.smtpPort || !newRow?.smtpUsername || !newRow?.smtpPassword) {
            enqueueSnackbar(handleValidateCodevalue(internationalise, "COMMON_MSG_MANDATORY_FIELDS"), { variant: "error" });
            handleReplyToEditClick(newRow?.id)();
            return newRow;
        } else {
            const rowWithType = { ...newRow, emailType: replyToType };
            isEditReplyToPage ? updateEmailCredentials(rowWithType) : saveEmailCredentials(rowWithType);
        }
        const updatedRow = { ...newRow, isNew: false };
        setReplyToDataGridData(prev => prev.map((row) => (row.id === newRow.id ? updatedRow : row)));
        return updatedRow;
    }, [enqueueSnackbar, handleReplyToEditClick, internationalise, isEditReplyToPage, replyToType, saveEmailCredentials, updateEmailCredentials]);

    const fetchReplyToImapFolders = useCallback(async (imapHost, imapPort, smtpUsername, smtpPassword, apiBase = 'api/client/email-config') => {
        try {
            setReplyToFoldersLoading(true);
            const url = `${process.env.REACT_APP_BASE_URL}${apiBase}/folders`;
            const payload = { imapHost, imapPort, smtpUsername, smtpPassword };
            const response = await createRequestData(payload, url);
            
            if (response?.data?.success && Array.isArray(response?.data?.data)) {
                setReplyToAvailableFolders(response.data.data.map(f => ({ name: f })));
            }
        } catch (e) {
            console.error('Failed to fetch Reply-To IMAP folders:', e);
        } finally {
            setReplyToFoldersLoading(false);
        }
    }, []);

    const handleReplyToFolderChange = useCallback((newValue) => {
        setReplyToSelectedFolders(newValue);
    }, []);

    const processReplyToIMapRowUpdate = useCallback(async (newRow, oldRow, apiBase = 'api/users/email-config', testSuffix = '/test-imap') => {
        if (!newRow?.imapHost || !newRow?.imapPort || !newRow?.smtpUsername || !newRow?.smtpPassword) {
            enqueueSnackbar(handleValidateCodevalue(internationalise, "COMMON_MSG_MANDATORY_FIELDS"), { variant: "error" });
            handleReplyToEditClick(newRow?.id)();
            return newRow;
        }
        
        try {
            setReplyToFoldersLoading(true);
            const testUrl = `${process.env.REACT_APP_BASE_URL}${apiBase}${testSuffix}`;
            const { id, ...testPayload } = newRow;
            const testResponse = await createRequestData(testPayload, testUrl);
            
            if (testResponse?.data?.success) {
                enqueueSnackbar(handleValidateCodevalue(internationalise, testResponse?.data?.message), { variant: "success" });
                await fetchReplyToImapFolders(newRow.imapHost, newRow.imapPort, newRow.smtpUsername, newRow.smtpPassword, apiBase);
                
                const updatedRow = { ...oldRow, ...newRow };
                setReplyToDataGridData(prev => prev.map((row) => (row.id === newRow.id ? updatedRow : row)));
                return updatedRow;
            } else {
                enqueueSnackbar(handleValidateCodevalue(internationalise, testResponse?.data?.message), { variant: "error" });
                handleReplyToEditClick(newRow?.id)();
                return newRow;
            }
        } catch (e) {
            enqueueSnackbar(handleValidateCodevalue(internationalise, "EMAIL_IMAP_CONNECTION_FAILED"), { variant: "error" });
            handleReplyToEditClick(newRow?.id)();
            return newRow;
        } finally {
            setReplyToFoldersLoading(false);
        }
    }, [enqueueSnackbar, handleReplyToEditClick, internationalise, fetchReplyToImapFolders]);

    const saveReplyToImapConfig = useCallback(async () => {
        const currentData = replyToDataGridData?.[0];
        if (!currentData?.imapHost || !currentData?.imapPort || !currentData?.smtpUsername || !currentData?.smtpPassword) {
            enqueueSnackbar(handleValidateCodevalue(internationalise, "COMMON_MSG_MANDATORY_FIELDS"), { variant: "error" });
            return false;
        }
        
        const emailMetadata = JSON.stringify({
            folder_list: replyToSelectedFolders.map(f => f.name)
        });
        
        const payload = {
            ...currentData,
            emailType: replyToType,
            emailMetadata
        };
        
        if (payload.id && typeof payload.id === 'string' && payload.id.startsWith('new-')) {
            delete payload.id;
        }
        
        try {
            if (isEditReplyToPage) {
                await updateEmailCredentials(payload);
            } else {
                await saveEmailCredentials(payload);
            }
            return true;
        } catch (e) {
            return false;
        }
    }, [selectedUser,replyToDataGridData, replyToSelectedFolders, replyToType, isEditReplyToPage, updateEmailCredentials, saveEmailCredentials, enqueueSnackbar, internationalise]);

    return {
        isLoading,
        internationalise,
        fromDataGridData,
        fromRowModesModel,
        handleFromEditClick,
        handleFromSaveClick,
        handleFromCancelClick,
        processFromRowUpdate,
        processIMapRowUpdate,
        replyToDataGridData,
        replyToRowModesModel,
        handleReplyToEditClick,
        handleReplyToSaveClick,
        handleReplyToCancelClick,
        processReplyToRowUpdate,
        updateEmailCredentials,
        saveEmailCredentials,
        isEditFromPage,
        sameAsFrom,
        handleSameAsFromChange,
        selectedFolders,
        setSelectedFolders,
        availableFolders,
        foldersLoading,
        fetchImapFolders,
        handleFolderChange,
        saveImapConfig,
        replyToSelectedFolders,
        setReplyToSelectedFolders,
        replyToAvailableFolders,
        replyToFoldersLoading,
        fetchReplyToImapFolders,
        handleReplyToFolderChange,
        processReplyToIMapRowUpdate,
        saveReplyToImapConfig,
        isEditReplyToPage,
    };
};
